import { Currency } from 'shared/components/Transactions/components';
import { ScheduledPaymentStatus } from 'shared/models/enums/ScheduledPaymentStatus';
import { PaymentType } from 'shared/models/PaymentType';

export const PaymentSourceExpiryStatus = {
  VALID: 0,
  EXPIRING: 1,
  EXPIRED: 2
};

export const checkExpired = paymentSource => {
  const expDate = new Date(paymentSource.expYear, paymentSource.expMonth, 0);
  const today = new Date();

  const expYear = expDate.getFullYear();
  const expMonth = expDate.getMonth();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  // Check if expired (past month/year)
  if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
    return PaymentSourceExpiryStatus.EXPIRED;
  }

  // Check if expiring (current month/year)
  if (expYear === currentYear && expMonth === currentMonth) {
    return PaymentSourceExpiryStatus.EXPIRING;
  }

  // Valid (future month/year)
  return PaymentSourceExpiryStatus.VALID;
};

export const getTotalCharge = (amount, sourceType, creditCardCharge) => {
  let totalCharge = parseFloat(amount);
  if (sourceType === PaymentType.Card) {
    totalCharge = parseFloat(amount) + parseFloat(creditCardCharge);
  }
  totalCharge = totalCharge.toFixed(2);
  return totalCharge;
};

export const getLeftOverBalance = (amount, amountDue) => {
  let leftOver = amount !== amountDue ? parseFloat(amountDue).toFixed(2) - amount : 0;
  leftOver = Math.abs(leftOver);
  return <Currency amount={leftOver} />;
};

export const checkAutopayStatus = lastAutopayStatus => {
  if (
    lastAutopayStatus &&
    (lastAutopayStatus === ScheduledPaymentStatus.Rescheduled || lastAutopayStatus === ScheduledPaymentStatus.Failed)
  ) {
    return lastAutopayStatus;
  }
  return null;
};

export const extractLifetimeSavings = datasets => {
  if (!Array.isArray(datasets)) {
    return null;
  }
  const savings = datasets.find(savings => savings.title.toLowerCase() === 'savings');
  return savings?.stats ? savings.stats.find(lifetime => lifetime.title.toLowerCase() === 'lifetime') : null;
};

export const PaymentActivityType = Object.freeze({
  SETUP_INTENT_REQUIRES_ACTION: 'setup_intent.requires_action',
  SETUP_INTENT_SETUP_FAILED: 'setup_intent.setup_failed',
  SETUP_INTENT_CREATED: 'setup_intent.created',
  SETUP_INTENT_CANCELED: 'setup_intent.canceled',
  PAYMENT_INTENT_SUCCEEDED: 'payment_intent.succeeded',
  PAYMENT_INTENT_PAYMENT_FAILED: 'payment_intent.payment_failed',
  PAYMENT_INTENT_REQUIRES_ACTION: 'payment_intent.requires_action',
  PAYMENT_INTENT_CREATED: 'payment_intent.created',
  PAYMENT_METHOD_ATTACHED: 'payment_method.attached',
  PAYMENT_METHOD_DETACHED: 'payment_method.detached',
  PAYMENT_METHOD_UPDATED: 'payment_method.updated',
  PAYMENT_METHOD_AUTOMATICALLY_UPDATED: 'payment_method.automatically_updated',
  CHARGE_DISPUTE_CREATED: 'charge.dispute.created',
  CHARGE_DISPUTE_UPDATED: 'charge.dispute.updated',
  CHARGE_DISPUTE_CLOSED: 'charge.dispute.closed',
  CHARGE_SUCCEEDED: 'charge.succeeded',
  CHARGE_FAILED: 'charge.failed',
  REFUND_UPDATED: 'refund.updated',
  REFUND_FAILED: 'refund.failed',
  PAYOUT_FAILED: 'payout.failed',
  FINANCIAL_CONNECTIONS_ACCOUNT_DEACTIVATED: 'financial_connections.account.deactivated',
  FINANCIAL_CONNECTIONS_ACCOUNT_REACTIVATED: 'financial_connections.account.reactivated',
  AUTOPAY_ENABLED: 'autopay.enabled',
  AUTOPAY_DISABLED: 'autopay.disabled',
  AUTOPAY_SUCCEEDED: 'autopay.succeeded',
  AUTOPAY_FAILED: 'autopay.failed',
  MANDATE_UPDATED: 'mandate.updated'
});

export const PaymentActivityMessage = Object.freeze({
  [PaymentActivityType.SETUP_INTENT_REQUIRES_ACTION]:
    'New payment method requires verification, likely microdeposits or bank login',
  [PaymentActivityType.SETUP_INTENT_SETUP_FAILED]: 'New payment method creation failed',
  [PaymentActivityType.SETUP_INTENT_CREATED]: 'New payment method submitted',
  [PaymentActivityType.SETUP_INTENT_CANCELED]:
    'Did not complete setting up a new payment method. Likely deleted the payment method before verifying microdeposits',
  [PaymentActivityType.PAYMENT_INTENT_SUCCEEDED]: 'Payment posted',
  [PaymentActivityType.PAYMENT_INTENT_PAYMENT_FAILED]: 'Payment failed to post',
  [PaymentActivityType.PAYMENT_INTENT_REQUIRES_ACTION]:
    'New payment method requires verification, likely microdeposits or bank login',
  [PaymentActivityType.PAYMENT_INTENT_CREATED]: 'New payment posted',
  [PaymentActivityType.PAYMENT_METHOD_ATTACHED]: 'New payment method linked',
  [PaymentActivityType.PAYMENT_METHOD_DETACHED]: 'Payment method removed',
  [PaymentActivityType.PAYMENT_METHOD_UPDATED]: 'Payment method edited',
  [PaymentActivityType.PAYMENT_METHOD_AUTOMATICALLY_UPDATED]: 'Payment method automatically edited',
  [PaymentActivityType.CHARGE_DISPUTE_CREATED]: 'Dispute created',
  [PaymentActivityType.CHARGE_DISPUTE_UPDATED]: 'Dispute updated',
  [PaymentActivityType.CHARGE_DISPUTE_CLOSED]: 'Dispute closed',
  [PaymentActivityType.CHARGE_SUCCEEDED]: 'Payment succeeded',
  [PaymentActivityType.CHARGE_FAILED]: 'Payment failed, unable to pull funds',
  [PaymentActivityType.REFUND_UPDATED]: 'Refund status updated',
  [PaymentActivityType.REFUND_FAILED]: 'Refund failed',
  [PaymentActivityType.PAYOUT_FAILED]: 'Payout failed',
  [PaymentActivityType.FINANCIAL_CONNECTIONS_ACCOUNT_DEACTIVATED]: 'Linked bank account removed or expired',
  [PaymentActivityType.FINANCIAL_CONNECTIONS_ACCOUNT_REACTIVATED]:
    'Previously linked bank account is now available again for use',
  [PaymentActivityType.AUTOPAY_ENABLED]: 'Autopay turned on',
  [PaymentActivityType.AUTOPAY_DISABLED]: 'Autopay turned off',
  [PaymentActivityType.AUTOPAY_SUCCEEDED]: 'Autopay succeeded',
  [PaymentActivityType.AUTOPAY_FAILED]: 'Autopay failed',
  [PaymentActivityType.MANDATE_UPDATED]: 'Customer bank account mandate updated'
});

export const getPaymentActivityMessage = (
  type,
  { amount, createdAt, failureMessage, outcomeReason, eventStatus, nextScheduledPaymentDate } = {}
) => {
  const baseMessage = PaymentActivityMessage[type];
  if (!baseMessage) return 'Unknown activity';
  outcomeReason = outcomeReason?.replace(/_/g, ' ').toLowerCase() || null;
  const prefix = amount
    ? `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — `
    : '';
  const amountMessage = amount
    ? ` for $${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '';
  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const eventDate = createdAt ? ` on ${formatDate(createdAt)}` : '';
  const retryMessage = nextScheduledPaymentDate
    ? ` Will retry on ${formatDate(nextScheduledPaymentDate)}.`
    : ' Will not retry.';

  switch (type) {
    case PaymentActivityType.SETUP_INTENT_SETUP_FAILED:
      return failureMessage ? `${baseMessage} — ${failureMessage}` : baseMessage;

    case PaymentActivityType.CHARGE_DISPUTE_UPDATED:
    case PaymentActivityType.REFUND_UPDATED:
      return `${prefix}${baseMessage}${outcomeReason ? ` — ${outcomeReason}` : ''}`;
    case PaymentActivityType.PAYMENT_METHOD_AUTOMATICALLY_UPDATED:
      return outcomeReason ? `${baseMessage} — ${outcomeReason}` : baseMessage;
    case PaymentActivityType.PAYOUT_FAILED:
      return `${baseMessage}${failureMessage ? ` — ${failureMessage}` : ''}${
        outcomeReason ? ` — ${outcomeReason}` : ''
      }`;

    case PaymentActivityType.PAYMENT_INTENT_PAYMENT_FAILED:
    case PaymentActivityType.CHARGE_FAILED:
    case PaymentActivityType.REFUND_FAILED:
      return `${prefix}${baseMessage}${failureMessage ? ` — ${failureMessage}` : ''}`;

    case PaymentActivityType.CHARGE_DISPUTE_CLOSED:
    case PaymentActivityType.MANDATE_UPDATED:
      return `${prefix}${baseMessage}${eventStatus ? ` — ${eventStatus}` : ''}`;
    case PaymentActivityType.AUTOPAY_SUCCEEDED:
      return `${baseMessage}${amountMessage}${eventDate}`;
    case PaymentActivityType.AUTOPAY_FAILED:
      return `${baseMessage}${eventDate}. ${failureMessage ? `Reason: ${failureMessage}` : ''}${retryMessage}`;
    case PaymentActivityType.FINANCIAL_CONNECTIONS_ACCOUNT_DEACTIVATED:
    case PaymentActivityType.FINANCIAL_CONNECTIONS_ACCOUNT_REACTIVATED:
    case PaymentActivityType.PAYMENT_METHOD_UPDATED:
    case PaymentActivityType.PAYMENT_INTENT_REQUIRES_ACTION:
    case PaymentActivityType.PAYMENT_INTENT_SUCCEEDED:
    case PaymentActivityType.PAYMENT_INTENT_CREATED:
    case PaymentActivityType.CHARGE_SUCCEEDED:
    case PaymentActivityType.CHARGE_DISPUTE_CREATED:
    default:
      return `${prefix}${baseMessage}`;
  }
};

export const formatCurrency = amount => {
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const formatNumberWithFixedDecimals = (number, decimals = 4) => {
  return number?.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
